import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  CssBaseline,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Typography,
  LinearProgress,
  Tooltip,
  Popover,
  Button,
  FormControlLabel,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DownloadIcon from '@mui/icons-material/Download';
import SettingsIcon from '@mui/icons-material/Settings';
import { bookmakerLogos } from '../assets/bookmakerLogos';
/* ---------- THEME ---------- */
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: {
    h5: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
          color: '#ffffff',
          borderRadius: 4,
          border: '1px solid #444',
        },
      },
    },
  },
});

/* ---------- HELPER COMPONENTS ---------- */
const NumericCell = ({ value }) => {
  if (value === '-' || value == null) return '-';
  const formatted = value >= 0 ? `+${value}` : `${value}`;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted.padStart(5, ' ')}
      </Typography>
    </Box>
  );
};

const PercentageCell = ({ value }) => {
  if (value == null) return '-';
  const val = value * 100;
  let red, green, blue;

  if (val <= -5) {
    red = 255;
    green = 0;
    blue = 0;
  } else if (val >= 5) {
    red = 0;
    green = 255;
    blue = 0;
  } else if (val < 0) {
    const ratio = (val + 10) / 10;
    red = 255;
    green = blue = Math.round(255 * ratio);
  } else {
    const ratio = val / 10;
    green = 255;
    red = blue = Math.round(255 * (1 - ratio));
  }

  const formatted = val.toFixed(1).padStart(val < 0 ? 4 : 3, ' ') + '%';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ color: `rgb(${red},${green},${blue})`, fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {formatted.padStart(val < 0 ? 6 : 5, ' ')}
      </Typography>
    </Box>
  );
};

const LineCell = ({ value }) => {
  if (value === '-' || value == null) return '-';
  const match = value.match(/^([ou])?(\d+(\.\d)?)$/);
  if (!match) return value;
  const [, prefix = '', number] = match;
  const formatted = number.padStart(3, ' ');
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', width: '100%' }}>
      <Typography sx={{ fontSize: '0.875rem', whiteSpace: 'pre', textAlign: 'left' }}>
        {prefix + formatted}
      </Typography>
    </Box>
  );
};

/* ---------- COMPONENT ---------- */
export default function BetTracker({ bets, userName, trackBet, untrackBet, trackedBets, setTrackedBets, ws, closingOdds }) {
  /* ---------- STATE ---------- */
  const [pageSize, setPageSize] = useState(50);
  const [sortModel, setSortModel] = useState([{ field: 'trackedAt', sort: 'desc' }]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [betTypeFilter, setBetTypeFilter] = useState([]);
  const [bookFilter, setBookFilter] = useState([]);
  const [betCategoryFilter, setBetCategoryFilter] = useState([]);
  
  const [closingOddsState, setClosingOddsState] = useState({});
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [hidePastBets, setHidePastBets] = useState(() => {
    const saved = localStorage.getItem('hidePastBets');
    return saved ? JSON.parse(saved) : false;
  });
  const requestedOddsIds = useRef(new Set());

  /* ---------- PERSIST SETTINGS ---------- */
  useEffect(() => {
    localStorage.setItem('hidePastBets', JSON.stringify(hidePastBets));
  }, [hidePastBets]);

  /* ---------- SETTINGS POPOVER HANDLERS ---------- */
  const handleSettingsOpen = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleSettingsSave = () => {
    setSettingsAnchorEl(null);
  };

  /* ---------- FETCH CLOSING ODDS ---------- */
  useEffect(() => {
    if (!ws || !userName || !trackedBets || !trackedBets.length) return;

    // Request closing odds for past bets only
    const pastBets = trackedBets.filter(bet => {
      return bet.eventStart && new Date(bet.eventStart) <= new Date();
    });

    const oddsIds = pastBets
      .map(bet => bet.oddsId)
      .filter(id => id && !requestedOddsIds.current.has(id));
    if (oddsIds.length === 0) return;

    ws.send(JSON.stringify({
      action: 'get_closing_odds',
      userName,
      oddsIds
    }));

    // Mark oddsIds as requested
    oddsIds.forEach(id => requestedOddsIds.current.add(id));
  }, [ws, userName, trackedBets]);

  /* ---------- UPDATE CLOSING ODDS STATE ---------- */
  useEffect(() => {
    if (!closingOdds || !Array.isArray(closingOdds)) return;

    setClosingOddsState(prev => {
      const newState = { ...prev };
      closingOdds.forEach(odds => {
        newState[odds.id] = {
          points: odds.points,
          price: odds.price,
          breakEvenWin: odds.breakEvenWin,
          trueWin: odds.trueWin,
          roi: odds.roi,
          eventStart: odds.eventStart,
          minutesToGameStart: odds.minutesToGameStart
        };
      });
      return newState;
    });
  }, [closingOdds]);

  /* ---------- PROCESS BETS ---------- */
  const processedBets = useMemo(() => {
    if (!Array.isArray(bets)) {
      return [];
    }
    return bets.map((row, idx) => {
      const id = row.id ?? `bet-${idx}-${Date.now()}`;
      let side = row.side || '';
      if (side === 'Over') side = 'o';
      if (side === 'Under') side = 'u';
      return {
        ...row,
        id,
        sidepoints: `${side}${row.points || ''}`.trim(),
        displayName: row.playerName?.trim() !== '' ? row.playerName : row.teamName || '',
      };
    });
  }, [bets]);

  /* ---------- DYNAMIC FILTER OPTIONS FROM TRACKED BETS ---------- */
  const dynamicLeagues = useMemo(
    () => [...new Set(trackedBets.map((b) => b.leagueName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicBetTypes = useMemo(
    () => [...new Set(trackedBets.map((b) => b.betTypeName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicBooks = useMemo(
    () => [...new Set(trackedBets.map((b) => b.marketSourceName).filter(Boolean))],
    [trackedBets]
  );
  const dynamicBetCategories = useMemo(
    () => [...new Set(trackedBets.map((b) => b.betCategory).filter(Boolean))],
    [trackedBets]
  );

  /* ---------- INITIALIZE FILTERS WITH ALL OPTIONS ---------- */
  useEffect(() => {
    setLeagueFilter(dynamicLeagues);
  }, [dynamicLeagues]);
  useEffect(() => {
    setBetTypeFilter(dynamicBetTypes);
  }, [dynamicBetTypes]);
  useEffect(() => {
    setBookFilter(dynamicBooks);
  }, [dynamicBooks]);
  useEffect(() => {
    setBetCategoryFilter(dynamicBetCategories);
  }, [dynamicBetCategories]);

  /* ---------- FILTER HELPERS ---------- */
  const handleFilterChange = (e, options, cur, setter) => {
    const val = e.target.value;
    if (val[val.length - 1] === 'all') {
      setter(cur.length === options.length ? [] : options);
    } else {
      setter(val);
    }
  };
  const renderFilterValue = (sel, opts, label) => {
    if (!sel.length) return label;
    if (sel.length === opts.length) return 'All';
    return sel.length > 1 ? `Multiple (${sel.length})` : sel.join(', ');
  };

  /* ---------- APPLY FILTERS AND DETERMINE TRACKED STATUS ---------- */
  const { filteredBets, avgEV, avgLiveEV, avgOddsDelta } = useMemo(() => {
    let allTrackedBets = [...trackedBets];

    // Create a map for faster lookup, using oddsId or a unique identifier
    const betMap = new Map(
      processedBets.map(bet => [bet.oddsId || bet.id, bet]) // Use oddsId if available, else id
    );

    allTrackedBets = allTrackedBets.map(trackedBet => {
      // Fetch live bet data using oddsId
      const liveBet = betMap.get(trackedBet.oddsId) || {};
      const closingOdd = closingOddsState[trackedBet.oddsId] || {};
      const isPastGame = trackedBet.eventStart && new Date(trackedBet.eventStart) <= new Date();

      let currentLine, currentOdds, liveEV, lineValue;

      if (isPastGame && closingOdd.points != null && closingOdd.price != null) {
        const side = trackedBet.side === 'Over' ? 'o' : trackedBet.side === 'Under' ? 'u' : '';
        currentLine = `${side}${closingOdd.points}`.trim();
        currentOdds = closingOdd.price;
        liveEV = closingOdd.roi != null && trackedBet.points === closingOdd.points
          ? closingOdd.trueWin/trackedBet.breakEvenWin -1
          : null;
        lineValue = closingOdd.breakEvenWin != null && trackedBet.points === closingOdd.points
          ? closingOdd.breakEvenWin / trackedBet.breakEvenWin -1
          : null;
      } else {
        // Use live bet data for future games
        const side = liveBet.side === 'Over' ? 'o' : liveBet.side === 'Under' ? 'u' : '';
        currentLine = liveBet.points != null ? `${side}${liveBet.points}` : '-';
        currentOdds = liveBet.price != null ? liveBet.price : '-';
        // Calculate liveEV and lineValue for future games
        liveEV = liveBet.trueWin != null && liveBet.breakEvenWin != null && Number(liveBet.points) === Number(trackedBet.points)
          ? (liveBet.trueWin / trackedBet.breakEvenWin) - 1
          : null;
        lineValue = (
          trackedBet.breakEvenWin != null &&
          liveBet.breakEvenWin != null &&
          Math.abs(Number(trackedBet.points) - Number(liveBet.points)) < 0.01
        ) ? (liveBet.breakEvenWin/trackedBet.breakEvenWin) - 1 : null;


      }

      return {
        id: trackedBet.id,
        roi: trackedBet.roi,
        sidepoints: trackedBet.sidepoints,
        price: trackedBet.price,
        currentLine,
        currentOdds,
        liveEV,
        lineValue,
        leagueName: trackedBet.leagueName,
        eventShortName: trackedBet.eventShortName,
        eventStart: trackedBet.eventStart,
        awayLogo: trackedBet.awayLogo,
        homeLogo: trackedBet.homeLogo,
        playerName: trackedBet.playerName,
        teamName: trackedBet.teamName,
        betTypeName: trackedBet.betTypeName,
        marketSourceName: trackedBet.marketSourceName,
        betCategory: trackedBet.betCategory,
        isPastGame,
        trackedAt: trackedBet.trackedAt,
      };
    });

    const leagueSet = new Set(leagueFilter);
    const betTypeSet = new Set(betTypeFilter);
    const bookSet = new Set(bookFilter);
    const betCategorySet = new Set(betCategoryFilter);

    const filtered = allTrackedBets.filter(bet => {
      const leagueMatch = leagueFilter.length === 0 || (bet.leagueName && leagueSet.has(bet.leagueName));
      const betTypeMatch = betTypeFilter.length === 0 || (bet.betTypeName && betTypeSet.has(bet.betTypeName));
      const bookMatch = bookFilter.length === 0 || (bet.marketSourceName && bookSet.has(bet.marketSourceName));
      const categoryMatch = betCategoryFilter.length === 0 || (bet.betCategory && betCategorySet.has(bet.betCategory));
      const pastBetMatch = !hidePastBets || (bet.eventStart && new Date(bet.eventStart) > new Date());
      return leagueMatch && betTypeMatch && bookMatch && categoryMatch && pastBetMatch;
    });

    let evSum = 0, liveEVSum = 0, oddsDeltaSum = 0;
    let evCount = 0, liveEVCount = 0, oddsDeltaCount = 0;

    filtered.forEach(bet => {
      if (bet.roi != null) {
        evSum += bet.roi * 100;
        evCount++;
      }
      if (bet.liveEV != null) {
        liveEVSum += bet.liveEV * 100;
        liveEVCount++;
      }
      if (bet.lineValue != null) {
        oddsDeltaSum += bet.lineValue * 100;
        oddsDeltaCount++;
      }
    });

    const finalFilteredBets = [...filtered].sort((a, b) => {
      const sortDirection = sortModel[0]?.sort === 'desc' ? -1 : 1;
      const field = sortModel[0]?.field || 'trackedAt';
      if (a[field] < b[field]) return -sortDirection;
      if (a[field] > b[field]) return sortDirection;
      return 0;
    });

    return {
      filteredBets: finalFilteredBets,
      avgEV: evCount > 0 ? (evSum / evCount).toFixed(1) : '-',
      avgLiveEV: liveEVCount > 0 ? (liveEVSum / liveEVCount).toFixed(1) : '-',
      avgOddsDelta: oddsDeltaCount > 0 ? (oddsDeltaSum / oddsDeltaCount).toFixed(1) : '-',
    };
  }, [processedBets, leagueFilter, betTypeFilter, bookFilter, betCategoryFilter, trackedBets, sortModel, closingOddsState, hidePastBets]);

  /* ---------- EXPORT TO CSV FUNCTION ---------- */
  const exportToCSV = () => {
    if (!filteredBets.length) return;

    const headers = [
      'Tracked Time',
      'League',
      'Event',
      'Event Start',
      'Participant Name',
      'Bet Type',
      'Book',
      'Line',
      'Live Line',
      'Odds',
      'Live Odds',
      'Odds ∆',
      'EV',
      'Live EV',
    ];

    const rows = filteredBets.map(bet => {
      const trackedAt = bet.trackedAt
        ? new Date(bet.trackedAt).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';

      const eventStart = bet.eventStart
        ? new Date(bet.eventStart).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', '')
        : '';
      const line = bet.sidepoints || '-';
      const currentLine = bet.currentLine || '-';
      const odds = bet.price != null ? (bet.price >= 0 ? `+${bet.price}` : bet.price) : '-';
      const currentOdds = bet.currentOdds != null ? (bet.currentOdds >= 0 ? `+${bet.currentOdds}` : bet.currentOdds) : '-';
      const lineValue = bet.lineValue != null ? `${(bet.lineValue * 100).toFixed(1)}%` : '-';
      const ev = bet.roi != null ? `${(bet.roi * 100).toFixed(1)}%` : '-';
      const liveEV = bet.liveEV != null ? `${(bet.liveEV * 100).toFixed(1)}%` : '-';

      return [
        trackedAt,
        bet.leagueName || '',
        bet.eventShortName || '',
        eventStart,
        bet.playerName || '',
        bet.betTypeName || '',
        bet.marketSourceName || '',
        line,
        currentLine,
        odds,
        currentOdds,
        lineValue,
        ev,
        liveEV,
      ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `bettracker_export_${new Date().toISOString().replace(/[:.]/g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* ---------- COLUMNS ---------- */
  const columns = [
    {
      field: 'trackedAt',
      headerName: 'Tracked At',
      flex: 1,
      minWidth: 160,
      valueFormatter: v => {
        if (!v) return '';
        const d = new Date(v);
        const opts = {
          year: '2-digit',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        };
        const [mdy, t] = d.toLocaleString(undefined, opts).split(', ');
        return `${mdy} ${t.toLowerCase()}`;
      }
    },
    {
      field: 'eventShortName',
      headerName: 'Event',
      flex: 2,
      minWidth: 175,
      renderCell: (params) => {
          if (!params.value) {
            return <Box sx={{ paddingLeft: '8px' }}>-</Box>;
          }
        const eventStart = params.row.eventStart
          ? new Date(params.row.eventStart).toLocaleString('en-US', {
              year: '2-digit',
              month: 'numeric',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', '')
          : '';
        const [awayTeam, homeTeam] = params.value.split('@');
        const leagueName = params.row.leagueName || '';
        
        return (
          <Box sx={{ whiteSpace: 'pre-line', textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: '8px', flexWrap: 'wrap' }}>
              {/* League name */}
              {leagueName && (
                <>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mr: 0 }}>
                    {leagueName}
                  </Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: '#ffffff', mx: 0.5 }}>
                   |
                  </Typography>
                </>
              )}
              {/* Away team */}
              {params.row.awayLogo && (
                <img
                  src={params.row.awayLogo}
                  alt={`${awayTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{awayTeam}</Typography>
              <Typography sx={{ fontSize: '0.75rem', mx: '2px', paddingBottom: '2px' }}>@</Typography>
              {/* Home team */}
              {params.row.homeLogo && (
                <img
                  src={params.row.homeLogo}
                  alt={`${homeTeam} Logo`}
                  style={{ width: 'auto', height: '14px', paddingBottom: '0px', paddingRight: '2px', objectFit: 'contain' }}
                  loading="lazy"
                />
              )}
              <Typography sx={{ fontSize: '0.875rem' }}>{homeTeam}</Typography>
            </Box>
            <Typography sx={{ fontSize: '0.875rem', paddingLeft: '8px', color: '#BBBBBB' }}>{eventStart}</Typography>
          </Box>
        );
      },
    },
    {
      field: 'playerName',
      headerName: 'Bet Info',
      flex: 2,
      minWidth: 165,
      renderCell: (params) => {
        const playerName = params.row.playerName || '';
        const betTypeName = params.row.betTypeName || '';
        return (
          <Box sx={{ textAlign: 'left', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', width: '100%' }}>
            <Tooltip title={playerName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {playerName}
              </Typography>
            </Tooltip>
            <Tooltip title={betTypeName} placement="top" arrow>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  paddingLeft: '8px',
                  color: '#BBBBBB',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {betTypeName}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
{
  field: 'marketSourceName',
  headerName: 'Book',
  flex: 1,
  minWidth: 70,
  sortable: false,
  renderCell: (p) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
      }}
    >
      <img
        src={bookmakerLogos[p.row.marketSourceName] || ''}
        alt="Logo"
        style={{ width: 72, height: 72, objectFit: 'contain' }}
        loading="lazy"
        onError={(e) => {
          e.target.style.display = 'none'; // Hide broken images
        }}
      />
    </div>
  ),
},
    {
      field: 'roi',
      headerName: 'EV',
      flex: 1,
      minWidth: 55,
      sortable: false,
      renderCell: (p) => <PercentageCell value={p.value} />,
    },
    {
      field: 'liveEV',
      headerName: 'Live',
      minWidth: 65,
      flex: 1,
      sortable: false,
      renderCell: (p) => <PercentageCell value={p.value} />,
    },
    {
      field: 'sidepoints',
      headerName: 'Line',
      minWidth: 60,
      flex: 1,
      sortable: false,
      renderCell: (p) => <LineCell value={p.value} />,
    },
    {
      field: 'currentLine',
      headerName: 'Live',
      minWidth: 60,
      flex: 1,
      sortable: false,
      renderCell: (p) => <LineCell value={p.value} />,
    },
    {
      field: 'price',
      headerName: 'Odds',
      minWidth: 60,
      flex: 1,
      sortable: false,
      renderCell: (p) => <NumericCell value={p.value} />,
    },
    {
      field: 'currentOdds',
      headerName: 'Live',
      minWidth: 60,
      flex: 1,
      sortable: false,
      renderCell: (p) => <NumericCell value={p.value} />,
    },
    {
      field: 'lineValue',
      headerName: 'CLV',
      minWidth: 60,
      flex: 1,
      sortable: false,
      renderCell: (p) => <PercentageCell value={p.value} />,
    },
    {
      field: 'untrack',
      headerName: '',
      sortable: false,
      width: 40,
      flex: 1,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <IconButton
            onClick={() => {
              if (typeof untrackBet === 'function') {
                untrackBet(params.row.id);
              } else {
                console.warn('untrackBet is not a function');
              }
            }}
            size="small"
            sx={{ color: 'red', '&:hover': { color: '#ff5252' } }}
            disabled={!untrackBet}
          >
            <CheckBoxIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  /* ---------- RENDER ---------- */
  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ width: '100%', height: '100vh', backgroundColor: 'background.default', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Filters bar */}
          <Box className="filter-section">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              {/* ------- filters ------- */}
              <Stack direction="row" spacing={2} alignItems="center">
                {[
                  { label: 'League', items: dynamicLeagues, state: leagueFilter, setter: setLeagueFilter },
                  { label: 'Category', items: dynamicBetCategories, state: betCategoryFilter, setter: setBetCategoryFilter },
                  { label: 'Bet Type', items: dynamicBetTypes, state: betTypeFilter, setter: setBetTypeFilter },
                  { label: 'Book', items: dynamicBooks, state: bookFilter, setter: setBookFilter },
                ].map(({ label, items, state, setter }) => (
                  <FormControl key={label} variant="outlined" size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id={`${label}-filter-label`} shrink sx={{ fontSize: '0.875rem' }}>
                      {label}
                    </InputLabel>
                    <Select
                      multiple
                      labelId={`${label}-filter-label`}
                      value={state}
                      label={label}
                      notched
                      onChange={(e) => handleFilterChange(e, items, state, setter)}
                      renderValue={(sel) => renderFilterValue(sel, items, label)}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="all">
                        <Checkbox checked={state.length === items.length} />
                        <ListItemText primary="Select All" sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                      </MenuItem>
                      {items.map((val) => (
                        <MenuItem key={val} value={val}>
                          <Checkbox checked={state.includes(val)} />
                          <ListItemText primary={val} sx={{ '& .MuiTypography-root': { fontSize: '0.875rem' } }} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
                {/* Metrics Display */}
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
                    Avg EV: <PercentageCell value={avgEV === '-' ? null : parseFloat(avgEV) / 100} />
                  </Typography>
                  <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
                    Avg Live EV: <PercentageCell value={avgLiveEV === '-' ? null : parseFloat(avgLiveEV) / 100} />
                  </Typography>
                  <Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>
                    Avg CLV: <PercentageCell value={avgOddsDelta === '-' ? null : parseFloat(avgOddsDelta) / 100} />
                  </Typography>
                </Stack>
              </Stack>

              {/* ------- controls ------- */}
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton onClick={handleSettingsOpen} sx={{ color: '#ffffff' }}>
                  <SettingsIcon sx={{ fontSize: '1.2rem' }} />
                </IconButton>
                <Tooltip title="Export to CSV">
                  <IconButton
                    onClick={exportToCSV}
                    sx={{
                      color: '#bbbbbb',
                      '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                    }}
                  >
                    <DownloadIcon />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>

          {/* Settings Popover */}
          <Popover
            open={Boolean(settingsAnchorEl)}
            anchorEl={settingsAnchorEl}
            onClose={handleSettingsClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={{ p: '12px 16px', width: 320, backgroundColor: '#1e1e1e', color: '#ffffff' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hidePastBets}
                    onChange={() => setHidePastBets(prev => !prev)}
                    sx={{ color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } }}
                  />
                }
                label={<Typography sx={{ color: '#ffffff', fontSize: '0.875rem' }}>Hide Past Bets</Typography>}
                sx={{ mb: 2 }}
              />
              <Stack direction="row" spacing={1} sx={{ justifyContent: 'flex-end' }}>
                <Button 
                  onClick={handleSettingsClose} 
                  sx={{ 
                    color: '#ffffff', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleSettingsSave} 
                  sx={{ 
                    color: '#ff1744', 
                    fontSize: '0.875rem', 
                    minWidth: '80px',
                    '&:hover': { backgroundColor: '#333333' },
                  }}
                >
                  Save
                </Button>
              </Stack>
            </Box>
          </Popover>

          {/* DataGrid */}
          <Box className="table-section">
            <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
              <DataGridPro
                rows={filteredBets}
                columns={columns}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
                rowsPerPageOptions={[50, 100, 250]}
                pagination
                disableColumnMenu
                disableSelectionOnClick
                hideFooterSelectedRowCount
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                components={{ Toolbar: GridToolbar, LoadingOverlay: LinearProgress }}
                getRowClassName={(params) => params.row.isPastGame ? 'past-bet-row' : ''}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#1e1e1e',
                    borderBottom: '2px solid #444',
                    position: 'sticky',
                    top: '56px',
                    zIndex: 2,
                  },
                  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                  '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    letterSpacing: '0.02rem',
                    color: '#ffffff !important',
                  },
                  '& .MuiDataGrid-cell': {
                    paddingY: '0px',
                    color: '#ffffff !important',
                    userSelect: 'none',
                    fontSize: '0.875rem',
                    borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                  },
                  '& .MuiDataGrid-cell:last-child': {
                    borderRight: 'none', // Remove border from last column
                  },
                  '& .MuiDataGrid-cell:focus': { outline: 'none' },
                  '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                  '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                  '& .MuiDataGrid-row.Mui-selected': {
                    backgroundColor: '#1a1a1a',
                    '&:hover': { backgroundColor: '#1a1a1a' },
                  },
                  '& .past-bet-row': {
                    backgroundColor: '#2a2a2a',
                    '&:hover': { backgroundColor: '#2a2a2a' },
                  },
                  '& .MuiDataGrid-columnSeparator': { 
                    visibility: 'visible',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                  '& .MuiDataGrid-toolbarContainer .MuiButtonBase-root': { 
                    color: '#ffffff !important',
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-iconButtonContainer': { 
                    color: '#ffffff !important',
                    fontSize: '0.875rem',
                  },
                  '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: '#ffffff !important',
                    fontSize: '0.875rem',
                  },
                  '& .MuiSelect-icon': { color: '#ffffff !important' },
                  '& .MuiDataGrid-menuIconButton': { color: '#ffffff !important' },
                  '& .MuiDataGrid-root': {
                    overflowY: 'auto !important',
                    margin: 0,
                    boxSizing: 'border-box',
                  },
                  '& .MuiDataGrid-virtualScroller': {
                    overflowY: 'auto !important',
                  },
                  '& .MuiDataGrid-main': {
                    padding: 0,
                    boxSizing: 'border-box',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    backgroundColor: '#1e1e1e',
                    color: '#ffffff !important',
                    position: 'sticky',
                    bottom: 0,
                    zIndex: 1,
                    boxSizing: 'border-box',
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}